import { richTextToPlainText } from '~/lib/richText';
import { parseFragment, serializeOuter, type DefaultTreeAdapterTypes } from 'parse5';
import type { ContractFieldDefinition, ContractTableDefinition, ContractTableValues, ProjectContractSignature } from '~/types/models';

export type ContractDocumentPart =
    | { type: 'content'; html: string }
    | { type: 'signature'; role: 'tenant' | 'client' }
    | { type: 'columns'; columns: ContractDocumentPart[][] };

const fieldPattern = /<span\b[^>]*data-contract-field="([a-z][a-z0-9_]{0,63})"[^>]*>[\s\S]*?<\/span>/gi;

export function contractFieldKey(label: string): string {
    return label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64);
}

export function contractTableKey(label: string): string {
    const key = contractFieldKey(label);
    return key && !/^[a-z]/.test(key) ? `table_${key}` : key;
}

export function escapeContractHtml(value: string): string {
    return value.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] || char);
}

export function safeHttpsUrl(value: string): string | null {
    try {
        const url = new URL(value.trim());
        return url.protocol === 'https:' && !url.username && !url.password ? url.toString() : null;
    } catch {
        return null;
    }
}

function trimHttpsUrlPunctuation(value: string): string {
    let result = value.replace(/[.,;:!?]+$/, '');
    const closingPairs: Array<[string, string]> = [[')', '('], [']', '['], ['}', '{']];
    for (const [closing, opening] of closingPairs) {
        while (result.endsWith(closing) && result.split(opening).length < result.split(closing).length) result = result.slice(0, -1);
    }
    return result;
}

function linkifyHttpsText(value: string): string {
    const parts: string[] = [];
    const urlPattern = /https:\/\/[^\s<>"']+/gi;
    let cursor = 0;
    for (const match of value.matchAll(urlPattern)) {
        const start = match.index || 0;
        const rawUrl = match[0];
        const linkText = trimHttpsUrlPunctuation(rawUrl);
        const href = safeHttpsUrl(linkText);
        if (!href || !linkText) continue;
        parts.push(
            escapeContractHtml(value.slice(cursor, start)),
            `<a href="${escapeContractHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeContractHtml(linkText)}</a>`,
            escapeContractHtml(rawUrl.slice(linkText.length)),
        );
        cursor = start + rawUrl.length;
    }
    parts.push(escapeContractHtml(value.slice(cursor)));
    return parts.join('');
}

export function contractFieldKeys(html: string): string[] {
    return [...new Set([...html.matchAll(new RegExp(fieldPattern))].map(match => match[1] || ''))].filter(Boolean);
}

export function contractTableKeys(html: string): string[] {
    return [...new Set([...html.matchAll(/\bdata-contract-table="([a-z][a-z0-9_]{0,63})"/gi)].map(match => match[1] || ''))].filter(Boolean);
}

export function updateContractTableLabel(html: string, key: string, label: string): string {
    const safeKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return html.replace(new RegExp(`<div\\b(?=[^>]*\\bdata-contract-table="${safeKey}")[^>]*>`, 'gi'), openingTag => {
        if (/\bdata-contract-table-label="[^"]*"/i.test(openingTag)) {
            return openingTag.replace(/\bdata-contract-table-label="[^"]*"/i, `data-contract-table-label="${escapeContractHtml(label)}"`);
        }
        return openingTag.replace(/>$/, ` data-contract-table-label="${escapeContractHtml(label)}">`);
    });
}

export function removeContractTableTokens(html: string, key: string): string {
    const safeKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return html.replace(new RegExp(`<div\\b(?=[^>]*\\bdata-contract-table="${safeKey}")[^>]*>[\\s\\S]*?<\\/div>`, 'gi'), '');
}

export function missingContractFields(html: string, fields: ContractFieldDefinition[], values: Record<string, string>): string[] {
    const used = contractFieldKeys(html);
    return used.filter(key => fields.find(field => field.key === key)?.required !== false && !String(values[key] || '').trim());
}

export function resolveContractFields(html: string, values: Record<string, string>, proposalReferenceLinks: Record<string, string> = {}): string {
    return html.replace(fieldPattern, (_match, key: string) => {
        const value = String(values[key] || '');
        const text = escapeContractHtml(value);
        const href = safeHttpsUrl(String(proposalReferenceLinks[key] || ''));
        return href
            ? `<a href="${escapeContractHtml(href)}" target="_blank" rel="noopener noreferrer">${text}</a>`
            : linkifyHttpsText(value);
    });
}

export function resolveContractTables(html: string, definitions: ContractTableDefinition[], values: ContractTableValues): string {
    const tablesByKey = new Map(definitions.map(table => [table.key, table]));
    return html.replace(/<div\b(?=[^>]*\bdata-contract-table="([a-z][a-z0-9_]{0,63})")[^>]*>[\s\S]*?<\/div>/gi, (token, key: string) => {
        const definition = tablesByKey.get(key);
        const rows = (values[key] || []).filter(row => definition?.columns.some(column => String(row?.[column.key] || '').trim()));
        if (!definition || !definition.columns.length || !rows.length) return '';
        const headings = definition.columns.map(column => `<th>${escapeContractHtml(column.label)}</th>`).join('');
        const body = rows.map(row => `<tr>${definition.columns.map(column => `<td>${escapeContractHtml(String(row?.[column.key] || '')).replace(/\r?\n/g, '<br>')}</td>`).join('')}</tr>`).join('');
        return `<table data-contract-table-output="${escapeContractHtml(key)}"><caption>${escapeContractHtml(definition.label)}</caption><thead><tr>${headings}</tr></thead><tbody>${body}</tbody></table>`;
    });
}

export function contractDocumentParts(html: string, values: Record<string, string>, tableDefinitions: ContractTableDefinition[] = [], tableValues: ContractTableValues = {}, proposalReferenceLinks: Record<string, string> = {}): ContractDocumentPart[] {
    const resolved = resolveContractTables(resolveContractFields(html, values, proposalReferenceLinks), tableDefinitions, tableValues);
    return parseNodes(parseFragment(resolved).childNodes);
}

function attribute(node: DefaultTreeAdapterTypes.Element, name: string): string | undefined {
    return node.attrs.find(attr => attr.name === name)?.value;
}

function parseNodes(nodes: DefaultTreeAdapterTypes.ChildNode[]): ContractDocumentPart[] {
    const parts: ContractDocumentPart[] = [];
    let content = '';
    const flush = () => {
        if (content && richTextToPlainText(content)) parts.push({ type: 'content', html: content });
        content = '';
    };
    for (const node of nodes) {
        if ('tagName' in node && node.tagName === 'div') {
            const role = attribute(node, 'data-contract-signature');
            if (role === 'tenant' || role === 'client') {
                flush();
                parts.push({ type: 'signature', role });
                continue;
            }
            if (attribute(node, 'data-editor-columns') !== undefined) {
                flush();
                const columns = node.childNodes
                    .filter((child): child is DefaultTreeAdapterTypes.Element => 'tagName' in child && attribute(child, 'data-editor-column') !== undefined)
                    .map(column => parseNodes(column.childNodes));
                if (columns.some(column => column.length)) parts.push({ type: 'columns', columns });
                continue;
            }
        }
        content += serializeOuter(node);
    }
    flush();
    return parts;
}

export function signatureForRole(role: 'tenant' | 'client', tenant?: ProjectContractSignature, client?: ProjectContractSignature) {
    return role === 'tenant' ? tenant : client;
}
